import axiosInstance from '../api/axiosInstance'
import { FLEXY_PRODUCTS_ALL_PATH } from '../constants/productsApiPaths'
import {
  buildProductStockExportRows,
  extractFlexyProductsFromExportResponse,
  parseProductsListPage,
  readCatalogName,
  readCatalogSku,
  writeProductStockWorkbook,
  type CatalogProductForStockExport
} from '../utils/exportProductStockExcel'
import type { FlexyProductStockRecord } from '../utils/productTableBodegasConfig'

const CATALOG_PAGE_LIMIT = 1000
const MAX_CATALOG_PAGES = 30

export interface ProductStockExportResult {
  productCount: number
  flexyProductCount: number
}

async function fetchCatalogProducts(signal?: AbortSignal): Promise<CatalogProductForStockExport[]> {
  const collected: CatalogProductForStockExport[] = []
  const seenSkus = new Set<string>()
  let totalPages: number | null = null

  for (let page = 1; page <= MAX_CATALOG_PAGES; page += 1) {
    if (totalPages != null && page > totalPages) break

    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(CATALOG_PAGE_LIMIT))
    const response = await axiosInstance.get(`/products?${params.toString()}`, { signal })
    const parsed = parseProductsListPage(response.data)
    if (parsed.totalPages != null) totalPages = parsed.totalPages
    if (parsed.items.length === 0) break

    let newCount = 0
    for (const item of parsed.items) {
      const sku = readCatalogSku(item)
      if (!sku || seenSkus.has(sku)) continue
      seenSkus.add(sku)
      newCount += 1
      collected.push({
        sku,
        name: readCatalogName(item),
        raw: item
      })
    }

    if (newCount === 0 || parsed.items.length < CATALOG_PAGE_LIMIT) break
  }

  return collected
}

async function fetchFlexyStockSnapshot(signal?: AbortSignal): Promise<FlexyProductStockRecord[]> {
  const response = await axiosInstance.get(FLEXY_PRODUCTS_ALL_PATH, { signal })
  return extractFlexyProductsFromExportResponse(response.data)
}

/**
 * Consulta catálogo y stock Flexy al momento de exportar.
 * No reutiliza el stock ya pintado en la lista.
 */
export async function downloadProductStockExcel(
  roleId: number | undefined | null,
  signal?: AbortSignal
): Promise<ProductStockExportResult> {
  const [catalog, flexyProducts] = await Promise.all([
    fetchCatalogProducts(signal),
    fetchFlexyStockSnapshot(signal)
  ])

  if (signal?.aborted) {
    throw new DOMException('Exportación cancelada', 'AbortError')
  }

  if (catalog.length === 0) {
    throw new Error('No hay productos para exportar.')
  }

  const rows = buildProductStockExportRows(catalog, flexyProducts, roleId)
  writeProductStockWorkbook(rows)
  return {
    productCount: rows.length,
    flexyProductCount: flexyProducts.length
  }
}
